import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import type { MarketplaceProfileMetadata, MarketplacePublicationPayload } from "@mm/metx-workbench.meteomatics.com";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export const MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE = 8;
const MARKETPLACE_IMAGE_FILE_TYPE = "marketplace_dashboard_image";

function fetchMarketplaceDashboardPage(offset: number, limit: number) {
  return Api.marketplace.getMarketplaceDashboards({ offset, limit });
}

async function csrfInit() {
  const csrf = await Api.security.getCsrfTokenApiV2SecurityCsrfTokenGet({
    credentials: "include",
  });
  const authToken = await Api.config?.accessToken?.("OAuth2", []);
  return {
    credentials: "include" as const,
    headers: {
      "X-CSRF-Token": csrf.csrf_token,
      Authorization: authToken || "",
    },
  };
}

export function useMarketplaceDashboard(profileId: number, options?: { enabled?: boolean; retry?: boolean }) {
  return useQuery({
    queryKey: [QueryKey.MARKETPLACE_DASHBOARD, profileId],
    queryFn: () => Api.marketplace.getMarketplaceDashboard({ profileId }),
    enabled: options?.enabled ?? true,
    retry: options?.retry,
  });
}

export function useMarketplaceDashboards() {
  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: [QueryKey.MARKETPLACE_DASHBOARDS],
    queryFn: ({ pageParam = 0 }) => fetchMarketplaceDashboardPage(pageParam, MARKETPLACE_DASHBOARDS_LIST_INITIAL_SIZE),
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.offset + lastPage.items.length : undefined),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);
  const lastPage = data?.pages.at(-1);
  const isEmpty = !isLoading && !isError && items.length === 0;

  return {
    isLoading,
    isError,
    isEmpty,
    items,
    total: lastPage?.total ?? 0,
    hasMore: hasNextPage ?? false,
    isLoadingMore: isFetchingNextPage,
    loadMore: () => fetchNextPage(),
    refetch,
  };
}

function useInvalidateMarketplaceDashboard() {
  const queryClient = useQueryClient();
  return (profileId: number) => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.MARKETPLACE_DASHBOARD, profileId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.MARKETPLACE_DASHBOARDS] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.MARKETPLACE_ASSET] });
  };
}

export function useUpdateMarketplaceDashboardMetadata() {
  const invalidate = useInvalidateMarketplaceDashboard();
  return useMutation({
    mutationFn: ({
      profileId,
      metadata,
    }: {
      profileId: number;
      metadata: MarketplaceProfileMetadata;
    }) =>
      Api.marketplace.updateMarketplaceDashboardMetadata({
        profileId,
        marketplaceProfileMetadata: metadata,
      }),
    onSuccess: (_data, { profileId }) => invalidate(profileId),
  });
}

export function useUpdateMarketplaceDashboardPublication() {
  const invalidate = useInvalidateMarketplaceDashboard();
  return useMutation({
    mutationFn: ({
      profileId,
      publication,
    }: {
      profileId: number;
      publication: MarketplacePublicationPayload;
    }) =>
      Api.marketplace.updateMarketplaceDashboardPublication({
        profileId,
        marketplacePublicationPayload: publication,
      }),
    onSuccess: (_data, { profileId }) => invalidate(profileId),
  });
}

export function useUploadMarketplaceDashboardImage() {
  const invalidate = useInvalidateMarketplaceDashboard();
  return useMutation({
    mutationFn: async ({ profileId, file }: { profileId: number; file: Blob }) => {
      const init = await csrfInit();
      return Api.marketplace.uploadMarketplaceDashboardImage({ profileId, file }, init);
    },
    onSuccess: (_data, { profileId }) => invalidate(profileId),
  });
}

export function useDeleteMarketplaceDashboardImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: number) => {
      const init = await csrfInit();
      const files = (await Api.file.listFiles({ fileType: MARKETPLACE_IMAGE_FILE_TYPE })) as Array<{
        id?: number;
        user_file_id?: number;
      }>;
      const match = files.find((file) => file.id === fileId);
      const userFileId = match?.user_file_id ?? fileId;
      await Api.file.deleteFile({ userFileId }, init);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.MARKETPLACE_ASSET] });
    },
  });
}
